import { useEffect, useRef } from "react";

/**
 * Custom hook to override browser back button behavior
 * Redirects to a specific URL when the back button is clicked, regardless of history
 *
 * @param redirectUrl - The URL to redirect to when back button is clicked
 */
const useBackButtonOverride = (redirectUrl: string) => {
  const isRedirecting = useRef(false);

  useEffect(() => {
    const handlePopState = () => {
      if (!isRedirecting.current) {
        isRedirecting.current = true;
        // Redirect to the specified URL when back button is clicked
        window.location.replace(redirectUrl);
      }
    };

    // Push a state entry on mount and whenever the component updates
    // This creates a "trap" in the history
    const pushState = () => {
      window.history.pushState({ preventBack: true }, "", window.location.href);
    };

    pushState();

    // Add event listener for popstate (triggered on back/forward button)
    window.addEventListener("popstate", handlePopState);

    // Push state again after the popstate listener is added
    // This ensures the trap is always active
    window.addEventListener("load", pushState);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("load", pushState);
    };
  }, [redirectUrl]);
};

export default useBackButtonOverride;
